import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Clock, Users, Filter, ArrowRight, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { events as mockEvents } from '../../data/mock';
import { MapLink } from '../ui/MapLink';
import { AddToCalendarButton } from '../ui/AddToCalendar';
import api, { formatApiErrorDetail } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';
import { toast } from 'sonner';

const categories = ['All', 'Club Events', 'Community'];

const DINNER_EVENT_ID = 'evt-christmas-dinner-2026-12';

const RSVPDialog = ({ event, open, onOpenChange, onSuccess }) => {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [guests, setGuests] = useState(0);
  const [dish, setDish] = useState('');
  const [dietaryNotes, setDietaryNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDinner = event?.id === DINNER_EVENT_ID;

  useEffect(() => {
    if (open && user) {
      setName(`${user.first_name} ${user.last_name}`.trim());
      setEmail(user.email);
    }
  }, [open, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post(`/events/${event.id}/rsvp`, { name, email, guests: Number(guests) || 0, dish, dietary_notes: dietaryNotes });
      toast.success(`You're registered for ${event.title}! See you there.`);
      onOpenChange(false);
      setName('');
      setEmail('');
      setGuests(0);
      setDish('');
      setDietaryNotes('');
      onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="rsvp-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#3c4a2a]">
            RSVP: {event.title}
          </DialogTitle>
          <DialogDescription>
            {event.time} • {event.location}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="rsvp-name" className="text-[#3c4a2a]">Full Name *</Label>
            <Input
              id="rsvp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="rsvp-name-input"
              className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
              placeholder="Your name"
            />
          </div>
          <div>
            <Label htmlFor="rsvp-email" className="text-[#3c4a2a]">Email Address *</Label>
            <Input
              id="rsvp-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="rsvp-email-input"
              className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <Label htmlFor="rsvp-guests" className="text-[#3c4a2a]">Additional Guests</Label>
            <Input
              id="rsvp-guests"
              type="number"
              min={0}
              max={10}
              value={guests}
              onChange={(e) => setGuests(e.target.value)}
              data-testid="rsvp-guests-input"
              className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
            />
          </div>
          {isDinner && (
            <>
              <div>
                <Label htmlFor="rsvp-dish" className="text-[#3c4a2a]">Dish to Share (optional)</Label>
                <Input
                  id="rsvp-dish"
                  value={dish}
                  onChange={(e) => setDish(e.target.value)}
                  data-testid="rsvp-dish-input"
                  className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                  placeholder="e.g., Pecan pie, green bean casserole"
                />
              </div>
              <div>
                <Label htmlFor="rsvp-dietary" className="text-[#3c4a2a]">Dietary Notes (optional)</Label>
                <Input
                  id="rsvp-dietary"
                  value={dietaryNotes}
                  onChange={(e) => setDietaryNotes(e.target.value)}
                  data-testid="rsvp-dietary-input"
                  className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                  placeholder="e.g., gluten-free, nut allergy"
                />
              </div>
            </>
          )}
          <Button
            type="submit"
            disabled={isSubmitting}
            data-testid="rsvp-submit-btn"
            className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white py-5"
          >
            {isSubmitting ? 'Registering...' : 'Confirm RSVP'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EventsPage = () => {
  const { c } = useContent();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [events, setEvents] = useState(mockEvents);
  const [rsvpEvent, setRsvpEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadEvents = React.useCallback(() => {
    api.get('/events').then((res) => { if (res.data?.length) setEvents(res.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const filteredEvents = selectedCategory === 'All'
    ? events
    : selectedCategory === 'Community'
    ? events.filter((event) => event.event_type === 'community')
    : events.filter((event) => event.event_type !== 'community');

  const openRSVP = (event) => {
    setRsvpEvent(event);
    setDialogOpen(true);
  };

  return (
    <main className="pt-52">
      {/* Hero Section */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              {c('events.hero.eyebrow', 'Community Calendar')}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-3 mb-6">
              {c('events.hero.title', 'Upcoming Events')}
            </h1>
            <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
              {c('events.hero.text', "From monthly meetings to special workshops and community service days, there's always something happening. Join us and be part of the movement.")}
            </p>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap items-center gap-3 mb-12">
            <Filter className="w-5 h-5 text-[#3c4a2a]/60" />
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                data-testid={`event-filter-${category.toLowerCase()}`}
                className={selectedCategory === category
                  ? 'bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white'
                  : 'border-[#5d6b3c]/30 text-[#3c4a2a] hover:bg-[#bd8cae]/20'
                }
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Events Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {filteredEvents.map((event) => {
              const eventDate = new Date(`${event.date}T12:00:00`);
              const isPast = new Date(`${event.date}T23:59:59`) < new Date();
              const spots = event.spots_available ?? event.spotsAvailable ?? 0;
              const isCommunity = event.event_type === 'community';
              const isTentative = !!event.tentative;
              
              return (
                <Card
                  key={event.id}
                  data-testid={`event-card-${event.id}`}
                  className={`shadow-md transition-all duration-300 overflow-hidden ${
                    isTentative
                      ? 'bg-gray-50 border border-gray-300'
                      : 'bg-[#bd8cae]/10 border-2 border-[#bd8cae]/60'
                  } ${isPast ? 'opacity-55 grayscale' : 'hover:shadow-xl'}`}
                >
                  <div className="flex flex-col sm:flex-row">
                    {/* Date Badge */}
                    <div className={`sm:w-32 text-white p-6 flex flex-col items-center justify-center ${
                      isTentative ? 'bg-gray-400' : 'bg-[#bd8cae]'
                    }`}>
                      <p className="text-4xl font-bold leading-none">
                        {eventDate.getDate()}
                      </p>
                      <p className="text-sm uppercase mt-1">
                        {eventDate.toLocaleString('default', { month: 'short' })}
                      </p>
                      <p className="text-xs opacity-80 mt-1">
                        {eventDate.getFullYear()}
                      </p>
                    </div>
                    
                    {/* Event Details */}
                    <CardContent className="flex-1 p-6">
                      <Badge
                        variant="secondary"
                        className={isTentative ? 'bg-gray-200 text-gray-600 mb-3' : 'bg-[#bd8cae]/30 text-[#a3688f] mb-3'}
                      >
                        {isCommunity ? (isTentative ? 'Community' : 'Community — Confirmed') : 'Club Event'}
                      </Badge>
                      {isTentative && (
                        <Badge
                          variant="secondary"
                          data-testid={`event-tentative-badge-${event.id}`}
                          className="bg-gray-100 text-gray-500 border border-gray-300 mb-3 ml-2"
                        >
                          Tentative — Not Confirmed
                        </Badge>
                      )}
                      {isPast && (
                        <Badge
                          variant="secondary"
                          data-testid={`event-past-badge-${event.id}`}
                          className="bg-[#3c4a2a]/10 text-[#3c4a2a]/60 mb-3 ml-2"
                        >
                          Past Event
                        </Badge>
                      )}
                      <h3 className={`font-serif text-xl mb-3 ${isTentative ? 'text-gray-600' : 'text-[#3c4a2a]'}`}>
                        {event.title}
                      </h3>
                      <p className={`text-sm mb-4 line-clamp-3 ${isTentative ? 'text-gray-500' : 'text-[#3c4a2a]/70'}`}>
                        {event.description}
                      </p>
                      
                      <div className={`space-y-2 text-sm mb-4 ${isTentative ? 'text-gray-500' : 'text-[#3c4a2a]/60'}`}>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-2" />
                          {event.time}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                          <MapLink location={event.location} testId={`event-map-link-${event.id}`} />
                        </div>
                        {!isCommunity && (
                          <div className="flex items-center" data-testid={`event-spots-${event.id}`}>
                            <Users className="w-4 h-4 mr-2" />
                            {spots} spots available
                          </div>
                        )}
                        {!isCommunity && (event.rsvp_count || 0) > 0 && (
                          <div className="flex items-center text-[#a3688f] font-medium" data-testid={`event-attending-${event.id}`}>
                            <Users className="w-4 h-4 mr-2" />
                            {event.rsvp_count} {event.rsvp_count === 1 ? 'woman' : 'women'} attending
                          </div>
                        )}
                      </div>
                      
                      {!isCommunity && (
                        <Button
                          onClick={() => openRSVP(event)}
                          disabled={isPast || spots <= 0}
                          data-testid={`event-rsvp-btn-${event.id}`}
                          className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white"
                        >
                          {isPast ? 'This Event Has Passed' : spots > 0 ? 'RSVP for This Event' : 'Event Full'}
                          {!isPast && spots > 0 && <ArrowRight className="w-4 h-4 ml-2" />}
                        </Button>
                      )}
                      {event.link && (
                        <a
                          href={event.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`event-link-${event.id}`}
                          className={`mt-2 flex items-center justify-center gap-1.5 w-full rounded-md border px-3 py-2 text-sm transition-colors ${
                            isTentative
                              ? 'border-gray-300 text-gray-600 hover:bg-gray-100'
                              : 'border-[#bd8cae] text-[#a3688f] hover:bg-[#bd8cae]/15'
                          }`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          {event.link_label || 'More Info from Organizer'}
                        </a>
                      )}
                      {!isCommunity && !isPast && (
                        <div className="mt-2">
                          <AddToCalendarButton event={event} testId={`event-calendar-btn-${event.id}`} />
                        </div>
                      )}
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>

          {filteredEvents.length === 0 && (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-[#bd8cae] mx-auto mb-4" />
              <h3 className="font-serif text-xl text-[#3c4a2a] mb-2">
                No events in this category
              </h3>
              <p className="text-[#3c4a2a]/60">
                Check back soon or explore other event categories.
              </p>
            </div>
          )}
        </div>
      </section>

      <RSVPDialog
        event={rsvpEvent}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={loadEvents}
      />

      {/* Host an Event CTA */}
      <section className="py-16 bg-gradient-to-br from-[#bd8cae]/30 to-[#bd8cae]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl text-[#3c4a2a] mb-4">
            {c('events.host.heading', 'Want to Host an Event?')}
          </h2>
          <p className="text-[#3c4a2a]/80 mb-8">
            {c('events.host.text', 'Members can propose and host events that align with our mission. Share your ideas and talents with our community.')}
          </p>
          <Link to="/contact">
            <Button
              variant="outline"
              className="border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white px-8"
            >
              Propose an Event
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default EventsPage;
